import { useTranslation } from "react-i18next";
import { Alert, Badge, Card, CardTitle } from "@/core/ui";
import type { DefinitionErrorItem } from "./api";

/** Author-facing validation results. Codes are translated, paths point at the faulty element. */
export function ValidationPanel({ errors }: { errors: DefinitionErrorItem[] | null }) {
  const { t } = useTranslation();
  const blocking = (errors ?? []).filter((e) => e.severity === "error");
  const warnings = (errors ?? []).filter((e) => e.severity !== "error");
  return (
    <Card>
      <CardTitle>{t("backlog.validation.title")}</CardTitle>
      <div aria-live="polite" className="bt-validation">
        {errors === null ? null : errors.length === 0 ? (
          <Alert tone="success">{t("backlog.validation.none")}</Alert>
        ) : (
          <>
            <div className="bt-validation__counts">
              {blocking.length > 0 ? (
                <Badge tone="danger">
                  {t("backlog.validation.blocking", { count: blocking.length })}
                </Badge>
              ) : null}
              {warnings.length > 0 ? (
                <Badge tone="warning">
                  {t("backlog.validation.warnings", { count: warnings.length })}
                </Badge>
              ) : null}
            </div>
            <ul className="bt-validation__list">
              {[...blocking, ...warnings].map((error, index) => (
                <li key={`${error.path}-${error.code}-${index}`} className="bt-validation__item">
                  <code className="bt-validation__path">{error.path}</code>
                  <span
                    className={
                      error.severity === "error"
                        ? "bt-validation__message--error"
                        : "bt-validation__message--warning"
                    }
                  >
                    {t(`backlog.definition_errors.${error.code}`, {
                      defaultValue: error.code,
                      ...error.params,
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </Card>
  );
}
